import React from 'react';
import { SeatDot, SeatData } from './SeatDot';
import { STATUS_COLORS, ZONE_COLORS } from './designTokens';

export interface TableData {
  id: string;
  tableNumber: number;
  tableName: string;
  zone: { name: string; colorCode: string };
  capacity: number;
  status: string;
  occupancy: number;
  reservedGroup?: string | null;
}

interface TableCircleProps {
  table: TableData;
  seats: SeatData[];
  isHighlighted: boolean;
  highlightedSeatIds: string[];
  hasVip: boolean;
  onClick: () => void;
  onMouseEnter: (event: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}

const TABLE_RADIUS = 40;
const SEAT_ORBIT_RADIUS = 48;

const STATUS_LABELS: Record<string, string> = {
  Available: 'Available',
  PartiallyReserved: 'Partially Reserved',
  FullyReserved: 'Fully Reserved',
  Held: 'Held',
  Blocked: 'Blocked',
};

function TableCircleInner({
  table,
  seats,
  isHighlighted,
  highlightedSeatIds,
  hasVip,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onKeyDown,
}: TableCircleProps) {
  const zoneColor = ZONE_COLORS[table.zone.name] || table.zone.colorCode;
  const statusColor = STATUS_COLORS[table.status] || '#999';
  const ariaLabel = `Table ${table.tableNumber}, ${table.occupancy} of ${table.capacity} seats occupied, status: ${STATUS_LABELS[table.status] || table.status}${hasVip ? ', VIP present' : ''}`;

  return (
    <g
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      style={{ cursor: 'pointer', outline: 'none' }}
      filter={isHighlighted ? 'url(#table-glow)' : undefined}
    >
      {/* Main circle */}
      <circle
        cx={0}
        cy={0}
        r={TABLE_RADIUS}
        fill="#ffffff"
        stroke={zoneColor}
        strokeWidth={3}
      />

      {/* Table number */}
      <text
        x={0}
        y={-6}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="700"
        fill="#1a2e23"
        fontFamily="Georgia, serif"
      >
        T{table.tableNumber}
      </text>

      {/* Occupancy */}
      <text
        x={0}
        y={10}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="10"
        fill="#4a5d52"
      >
        {table.occupancy}/{table.capacity}
      </text>

      {/* Status indicator dot */}
      <circle
        cx={TABLE_RADIUS - 8}
        cy={-(TABLE_RADIUS - 8)}
        r={5}
        fill={statusColor}
      />

      {/* VIP star */}
      {hasVip && (
        <text
          x={-(TABLE_RADIUS - 8)}
          y={-(TABLE_RADIUS - 10)}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
        >
          ⭐
        </text>
      )}

      {/* Seat dots */}
      {seats.map((seat, index) => (
        <SeatDot
          key={seat.id}
          seat={seat}
          index={index}
          totalSeats={seats.length}
          tableRadius={SEAT_ORBIT_RADIUS}
          isHighlighted={highlightedSeatIds.includes(seat.id)}
        />
      ))}
    </g>
  );
}

export const TableCircle = React.memo(TableCircleInner);
